import { useEffect, useRef } from 'react'

/**
 * TASTO INDIETRO (Android / gesto di ritorno)
 *
 * Problema risolto: l'app non usa il routing, quindi ogni schermata vive nello
 * stesso URL. Su una PWA installata, premere "indietro" usciva dall'app invece
 * di tornare alla schermata precedente.
 *
 * Come funziona: ogni vista "profonda" che si apre inserisce una voce nella
 * cronologia del browser e registra la propria funzione di chiusura in una pila.
 * Quando l'utente preme indietro, il browser emette popstate e noi eseguiamo
 * SOLO la chiusura della vista più in alto nella pila. In home la pila è vuota
 * e il tasto torna a chiudere l'app, come l'utente si aspetta.
 *
 * REGOLA per chi usa useIndietro: la funzione passata deve essere LA STESSA che
 * usa la freccia dell'app per quella vista, non una riscrittura. Le chiusure
 * hanno spesso effetti collaterali (reset del tab, ricarica dati, avvisi di
 * conferma): riscriverle a parte significa dimenticarne qualcuno.
 *
 * Rifiuto di chiusura: se la funzione restituisce `false`, la vista resta aperta
 * e rimane registrata. Serve quando la chiusura mostra prima un avviso (es.
 * "Sessione in corso — esci comunque?"): il tasto indietro apre l'avviso, e se
 * l'utente sceglie di restare, il tasto indietro successivo funziona ancora.
 */

let pila = []            // [{ id, chiudi }] — la più recente in fondo
let ascoltatoreAttivo = false
// Quante popstate successive sono generate da noi (chiusure dall'interno) e non
// dall'utente. È un contatore e non un sì/no perché più viste possono chiudersi
// nello stesso istante (es. al cambio schermata): ognuna produce la sua popstate.
let popDaIgnorare = 0

function gestisciPop() {
  if (popDaIgnorare > 0) { popDaIgnorare--; return }
  const voce = pila.pop()
  if (!voce) return   // pila vuota: siamo in home, il browser esce dall'app
  let esito
  try { esito = voce.chiudi() } catch { /* una chiusura fallita non deve bloccare la navigazione */ }
  if (esito === false) {
    // La vista ha rifiutato di chiudersi (sta mostrando un avviso): resta in cima
    // alla pila e le ridiamo la sua voce di cronologia, appena consumata dal browser.
    pila.push(voce)
    window.history.pushState({ fp: pila.length }, '')
  }
  // Nessun altro pushState: voci di cronologia e viste aperte restano 1 a 1.
}

function attivaAscoltatore() {
  if (ascoltatoreAttivo) return
  window.addEventListener('popstate', gestisciPop)
  ascoltatoreAttivo = true
}

function apriVista(chiudi) {
  attivaAscoltatore()
  const id = Symbol('vista')
  window.history.pushState({ fp: pila.length + 1 }, '')
  pila.push({ id, chiudi })
  return id
}

function chiudiVista(id) {
  const i = pila.findIndex(v => v.id === id)
  if (i === -1) return   // già tolta dal tasto indietro: niente da fare
  const quante = pila.length - i   // questa vista e tutte quelle aperte sopra
  pila.splice(i)
  popDaIgnorare++                  // history.go produce una sola popstate
  window.history.go(-quante)
}

/**
 * Registra una vista nella navigazione indietro.
 *
 * @param {boolean}  attiva      true quando la vista è visibile
 * @param {function} onIndietro  la STESSA funzione della freccia dell'app.
 *                               Restituire false per non chiudere (es. se mostra un avviso).
 */
export function useIndietro(attiva, onIndietro) {
  // In un ref: la pila usa sempre l'ultima versione della funzione (con lo stato
  // aggiornato), senza reinserire voci di cronologia a ogni render.
  const rif = useRef(onIndietro)
  rif.current = onIndietro

  useEffect(() => {
    if (!attiva) return
    const id = apriVista(() => (rif.current ? rif.current() : undefined))
    return () => chiudiVista(id)
  }, [attiva])
}
